/**
 * Pure cell-summary logic for the "Accesso ai dati" grid (access-model A).
 *
 * `summarizeTableAccess` reduces a role's `DataTablePolicy` on ONE table to a
 * compact `CellSummary` the grid renders as a glyph — default-open aware
 * (A.0): NO policy = full access, an `none`-mode policy = denied, `filtered`
 * = rows restricted, `column_grants` = fields hidden. The grid + the docked
 * panel both read this single source so the overview can't drift from the
 * editor. Unit-tested next to `policyControls`'s `policyToMode`.
 */
import type { PolicyOut, PolicyPredicate } from "@/lib/api-apps";

/** What a table cell shows at a glance (default-open). */
export type AccessState =
  | "full" // no policy or mode=full, no field/row restriction
  | "restricted" // rows and/or fields narrowed (still some access)
  | "none"; // explicit deny (mode=none)

export interface CellSummary {
  state: AccessState;
  /** # of columns the role can't see (column_grants hidden). */
  hiddenCount: number;
  /** rows narrowing, for the tooltip + the filter glyph: null = all rows. */
  rows: "own" | "filter" | null;
  /** insert/update/delete narrowed below the default — surfaced in the tooltip. */
  writeLimited: boolean;
}

const FULL: CellSummary = { state: "full", hiddenCount: 0, rows: null, writeLimited: false };

function isOwnPredicate(p: PolicyPredicate | null | undefined): boolean {
  return !!p && p.column === "created_by" && p.op === "eq" && p.value === "$me";
}

function countHidden(grants: Record<string, string> | null | undefined): number {
  if (!grants) return 0;
  return Object.values(grants).filter((g) => g === "hidden").length;
}

/**
 * A role's effective access to ONE table, given its policy (or `undefined`
 * when it has none → default-open full access).
 */
export function summarizeTableAccess(policy: PolicyOut | undefined): CellSummary {
  if (!policy) return FULL;
  if (policy.mode === "none") return { state: "none", hiddenCount: 0, rows: null, writeLimited: false };

  const hiddenCount = countHidden(policy.column_grants);
  const rows: CellSummary["rows"] =
    policy.mode === "filtered" ? (isOwnPredicate(policy.predicate) ? "own" : "filter") : null;
  // Writes are narrower than the default when insert is denied or update is
  // explicitly "none" (delete defaults to none, so it isn't a signal on its own).
  const writeLimited = policy.insert_mode === "none" || policy.update_mode === "none";

  const restricted = rows !== null || hiddenCount > 0 || writeLimited;
  return {
    state: restricted ? "restricted" : "full",
    hiddenCount,
    rows,
    writeLimited,
  };
}

/** A short Italian summary phrase for a table cell (tooltip + a11y label). */
export function describeTableAccess(s: CellSummary): string {
  if (s.state === "none") return "Nessun accesso";
  const parts: string[] = [];
  parts.push(s.rows === "own" ? "solo le proprie righe" : s.rows === "filter" ? "righe filtrate" : "tutte le righe");
  if (s.hiddenCount > 0) parts.push(`${s.hiddenCount} ${s.hiddenCount === 1 ? "campo nascosto" : "campi nascosti"}`);
  if (s.writeLimited) parts.push("scritture limitate");
  return parts.join(" · ");
}
